import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormRule } from 'antd';
import { Select, Space } from 'antd';
import { Form, Input, InputNumber } from 'antd';
import { useGetClient } from 'api/clients/use-get-client.ts';
import { useGetContact } from 'api/contacts/use-get-contact';
import type { ContactRes } from 'api/swagger';
import { ClientPaymentTerms } from 'types/client';
import { ClientFormSelect } from 'components/common/client-form-select';
import { ContactFormSelect } from 'components/common/contact-form-select';
import { FormCurrencySelect } from 'components/common/form-currency-select/form-currency-select';
import { CreateContact } from 'components/contacts/create-contact';
import type { IOrderFormProps, IOrderFormFields } from 'components/orders/order-form/types';
import styles from './order-form.module.scss';

const validationRule: Record<keyof IOrderFormFields, FormRule[]> = {
  client: [{ required: true, type: 'object' }],
  contact: [{ required: true, type: 'object' }],
  freight: [{ required: true, type: 'number', min: 1 }],
  documentPrice: [{ required: false, type: 'number', min: 0 }],
  fine: [{ required: false, type: 'integer', min: 0 }],
  clientOrderNumber: [{ required: false, type: 'string' }],
  currency: [{ required: true, type: 'string' }],
  paymentTerms: [{ required: true, type: 'string', enum: Object.values(ClientPaymentTerms) }],
  paymentPeriod: [{ required: true, type: 'integer' }],
};

export const OrderForm: React.FC<IOrderFormProps> = (props) => {
  const { form, formId, onFinish, initialValues, disableClientInput = false, withFine = false, footerRender } = props;
  const { t } = useTranslation();

  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [contactId, setContactId] = useState<number | undefined>(undefined);

  const { data: client } = useGetClient(clientId);
  const { data: contact } = useGetContact(contactId);

  const formClient = Form.useWatch('client', form);
  const formContact = Form.useWatch('contact', form);

  const handleClientChange = (option: { value: number; label: string }) => {
    form.setFieldValue('contact', undefined);
    form.setFieldValue('client', { value: option.value, label: option.label });
  };

  const handleContactChange = (option: { value: string; label: string }) => {
    form.setFieldValue('contact', { value: option.value, label: option.label });
  };

  const handleCreteContact = (contact: ContactRes) => {
    form.setFieldValue('contact', { value: contact.id, label: contact.name });
    form.validateFields(['contact']);
  };

  useEffect(() => {
    setClientId(Number(formClient?.value));
  }, [formClient]);

  useEffect(() => {
    setContactId(Number(formContact?.value));
  }, [formContact]);

  useEffect(() => {
    if (!initialValues && client) {
      form.setFieldValue('paymentTerms', client.paymentTerms);
      form.setFieldValue('paymentPeriod', client.paymentPeriod);
    }
  }, [client, form]);

  return (
    <Form form={form} id={formId} layout='vertical' initialValues={initialValues} onFinish={onFinish}>
      <ClientFormSelect
        label='Client'
        name='client'
        placeholder='Select Client'
        rules={validationRule.client}
        handleClientChange={handleClientChange}
        disabled={disableClientInput}
      />
      <div className={styles.margin}>
        <ContactFormSelect
          label='Contact'
          name='contact'
          placeholder='Select contact'
          rules={validationRule.contact}
          clientId={clientId}
          handleContactChange={handleContactChange}
          className={styles.withoutMargin}
        />
        {clientId ? (
          <CreateContact
            clientId={clientId}
            bthLabel={t('contact:create_new_contact')}
            textBtn
            onCreate={handleCreteContact}
          />
        ) : null}
      </div>
      <Space.Compact block>
        <Form.Item name='paymentTerms' style={{ width: '30%' }} rules={validationRule.paymentTerms}>
          <Select placeholder={t('client:form_payment_terms')} disabled={!clientId}>
            <Select.Option value={ClientPaymentTerms.scan}>{t('client:payment_terms_scan')}</Select.Option>
            <Select.Option value={ClientPaymentTerms.original}>{t('client:payment_terms_original')}</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name='paymentPeriod' style={{ width: '70%' }} rules={validationRule.paymentPeriod}>
          <InputNumber
            placeholder={t('client:form_payment_period')}
            decimalSeparator={','}
            type={'number'}
            style={{ width: '100%' }}
            disabled={!clientId}
          />
        </Form.Item>
      </Space.Compact>

      <Form.Item label='Freight' name='freight' rules={validationRule.freight}>
        <InputNumber
          placeholder='Freight'
          type='number'
          className={styles.inputFullWidth}
          addonAfter={<FormCurrencySelect />}
          disabled={!clientId}
        />
      </Form.Item>
      <Form.Item label={t('order:document_price')} name='documentPrice' rules={validationRule.documentPrice}>
        <InputNumber
          placeholder={t('order:document_price')}
          type='number'
          className={styles.inputFullWidth}
          addonAfter={<FormCurrencySelect />}
          disabled={!clientId}
        />
      </Form.Item>
      {withFine ? (
        <Form.Item label='Fine' name='fine' rules={validationRule.fine}>
          <InputNumber
            placeholder='Fine'
            type='number'
            className={styles.inputFullWidth}
            addonAfter={<FormCurrencySelect />}
            disabled={!clientId}
          />
        </Form.Item>
      ) : null}

      <Form.Item
        label={t('client:client_order_number')}
        name='clientOrderNumber'
        rules={validationRule.clientOrderNumber}>
        <Input placeholder={t('client:client_order_number')} className={styles.inputFullWidth} disabled={!clientId} />
      </Form.Item>

      {footerRender ? footerRender(client, contact) : null}
    </Form>
  );
};
